import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { playAudioFromPath } from '../lib/audio';
import type { MessageRow } from '../lib/messages';
import type { CrewMember } from '../state/session';
import { crewColor, hexWithAlpha, SELF_COLOR } from '../lib/crewColor';
import { routeMessage } from '../shared/routing';
import { supabase } from '../lib/supabase';
import { colors } from '../theme';

// Simpler than native — scrollable transcript list, search, tap-to-replay.
// "Catch me up" summaries and the 2-hour Log timeline are v1.2; this gives
// testers enough to scroll back and see what's been said.

type Props = {
  visible: boolean;
  onClose: () => void;
  messages: MessageRow[];
  crew: CrewMember[];
  selfId: string;
  groupId: string | null;
  onWhisperBack?: (senderId: string) => void;
};

const LONG_PRESS_MS = 500;

function timeAgo(iso: string): string {
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(iso).toLocaleDateString();
}

export function MessagesScreen({
  visible,
  onClose,
  messages,
  crew,
  selfId,
  groupId,
  onWhisperBack,
}: Props) {
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const crewById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const c of crew) m[c.id] = c.display_name;
    return m;
  }, [crew]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Upstream hook sorts DESC (newest first). Reverse for chat-style
    // layout where newest sits at the bottom.
    const base = q
      ? messages.filter((m) => {
          const name = crewById[m.sender_id]?.toLowerCase() ?? '';
          const text = m.transcript?.toLowerCase() ?? '';
          return name.includes(q) || text.includes(q);
        })
      : messages;
    return [...base].reverse();
  }, [messages, crewById, query]);

  // Auto-scroll to the bottom when the sheet opens or new messages arrive.
  const listRef = useRef<HTMLDivElement | null>(null);
  const lastCountRef = useRef(0);
  useLayoutEffect(() => {
    if (!visible) return;
    const el = listRef.current;
    if (!el) return;
    const grew = filtered.length > lastCountRef.current;
    lastCountRef.current = filtered.length;
    // Stick to bottom if the user is already near it, or always when opening.
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (grew || nearBottom) {
      el.scrollTop = el.scrollHeight;
    }
  }, [filtered, visible]);

  if (!visible) return null;

  async function replay(m: MessageRow) {
    if (!m.audio_path) return;
    try {
      await playAudioFromPath(m.audio_path);
    } catch (e) {
      console.warn('replay failed', e);
    }
  }

  async function sendText() {
    const text = draft.trim();
    if (!text || !groupId || sending) return;
    setSending(true);
    setSendError(null);
    try {
      const routed = routeMessage(text, crew, selfId);
      const { error } = await supabase.from('messages').insert({
        group_id: groupId,
        sender_id: selfId,
        recipient_id: routed.recipientId,
        audio_path: null,
        transcript: routed.displayTranscript,
        duration_ms: null,
        urgency: routed.urgency,
      });
      if (error) throw error;
      setDraft('');
    } catch (e: any) {
      setSendError(e?.message ?? String(e));
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="msg-scrim" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="msg-sheet">
        <div className="msg-header">
          <h2>Messages</h2>
          <button onClick={onClose} className="msg-done">
            Done
          </button>
        </div>
        <input
          className="msg-search"
          placeholder="Search transcripts or senders"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="msg-list" ref={listRef}>
          {filtered.length === 0 && <div className="msg-empty">No messages yet.</div>}
          {filtered.map((m) => {
            const name = crewById[m.sender_id] ?? 'Someone';
            const preview = m.transcript?.trim() || '🎤 voice message';
            const toSelf = m.recipient_id === selfId;
            const fromSelf = m.sender_id === selfId;
            const direct = m.recipient_id !== null;
            const urgent = m.urgency === 'urgent';
            const recipientName = m.recipient_id
              ? crewById[m.recipient_id] ?? 'someone'
              : null;
            const canWhisperBack = !fromSelf && !!onWhisperBack;
            const pinColor = fromSelf ? SELF_COLOR : crewColor(m.sender_id);
            // Urgent overrides the per-sender tint so "fire in the barn" is
            // visually unmissable regardless of whose pin color would apply.
            const bubbleBg = urgent ? colors.danger : hexWithAlpha(pinColor, 0.22);
            const accentStripe = urgent ? colors.danger : pinColor;
            return (
              <div
                key={m.id}
                className={`msg-lane ${fromSelf ? 'msg-lane-self' : 'msg-lane-other'}`}
              >
                <div className="msg-meta-outer">
                  <span className="msg-from">
                    {urgent ? '🚨 ' : ''}
                    {name}
                    {fromSelf ? ' (you)' : ''}
                    {recipientName ? ` → ${recipientName}${toSelf ? ' (you)' : ''}` : ''}
                  </span>
                  <span className="msg-time">
                    {m.audio_path && <span className="msg-voice-dot" title="voice message">🎤</span>}
                    {timeAgo(m.created_at)}
                  </span>
                </div>
                <MessageRowButton
                  urgent={urgent}
                  direct={direct}
                  toSelf={toSelf}
                  fromSelf={fromSelf}
                  canWhisperBack={canWhisperBack}
                  hasAudio={!!m.audio_path}
                  bubbleBg={bubbleBg}
                  accentStripe={accentStripe}
                  onReplay={() => replay(m)}
                  onWhisperBack={
                    canWhisperBack ? () => onWhisperBack!(m.sender_id) : undefined
                  }
                >
                  <div className="msg-body">{preview}</div>
                  {!m.audio_path && <div className="msg-expired">Audio expired</div>}
                  {canWhisperBack && <div className="msg-hint">Long-press to whisper back</div>}
                </MessageRowButton>
              </div>
            );
          })}
        </div>
        {sendError && <div className="msg-send-error">{sendError}</div>}
        <form
          className="msg-composer"
          onSubmit={(e) => {
            e.preventDefault();
            sendText();
          }}
        >
          <input
            className="msg-composer-input"
            placeholder={groupId ? 'Message…' : 'Join a group first'}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={!groupId || sending}
            enterKeyHint="send"
          />
          <button
            type="submit"
            className="msg-send-btn"
            aria-label="Send message"
            disabled={!draft.trim() || !groupId || sending}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path fill="currentColor" d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
            </svg>
          </button>
        </form>
      </div>

      <style>{`
        .msg-scrim {
          position: fixed; inset: 0;
          background: rgba(0,0,0,0.6);
          z-index: 2400;
          display: flex; align-items: flex-end;
        }
        .msg-sheet {
          width: 100%;
          background: ${colors.bg};
          border-radius: 16px 16px 0 0;
          padding: calc(env(safe-area-inset-top) + 8px) 12px
                   calc(env(safe-area-inset-bottom) + 24px);
          max-height: 90vh;
          display: flex; flex-direction: column;
        }
        .msg-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 10px 4px;
          border-bottom: 1px solid ${colors.divider};
        }
        .msg-header h2 { margin: 0; color: ${colors.text}; font-size: 20px; }
        .msg-done {
          background: transparent;
          color: ${colors.accent};
          border: none;
          font-size: 15px;
          font-weight: 600;
          padding: 10px 8px;
          min-height: 44px;
          min-width: 44px;
        }
        .msg-search {
          margin: 10px 0;
          padding: 10px 12px;
          background: ${colors.surface};
          color: ${colors.text};
          border: 1px solid ${colors.divider};
          border-radius: 10px;
          font-size: 14px;
        }
        .msg-search:focus { outline: none; border-color: ${colors.accent}; }
        .msg-list {
          flex: 1;
          overflow-y: auto;
          display: flex; flex-direction: column; gap: 10px;
          padding-bottom: 8px;
        }
        .msg-empty { color: ${colors.textMuted}; padding: 24px; text-align: center; font-size: 13px; }
        .msg-lane {
          display: flex; flex-direction: column;
          max-width: 78%;
          gap: 3px;
        }
        .msg-lane-self { align-self: flex-end; align-items: flex-end; }
        .msg-lane-other { align-self: flex-start; align-items: flex-start; }
        .msg-meta-outer {
          display: flex; gap: 8px;
          font-size: 11px;
          padding: 0 4px;
          color: ${colors.textMuted};
        }
        .msg-from { font-weight: 700; color: ${colors.text}; }
        .msg-time { white-space: nowrap; }
        .msg-row {
          display: flex; flex-direction: column; gap: 2px;
          color: ${colors.text};
          border: none;
          border-radius: 16px;
          padding: 10px 14px;
          text-align: left;
          font-family: inherit;
          width: 100%;
        }
        .msg-lane-self .msg-row { border-bottom-right-radius: 4px; }
        .msg-lane-other .msg-row { border-bottom-left-radius: 4px; }
        .msg-row:active { filter: brightness(1.15); }
        .msg-row:disabled { opacity: 0.6; }
        .msg-direct { box-shadow: inset 0 0 0 1px ${colors.danger}; }
        .msg-to-self { box-shadow: inset 0 0 0 1px ${colors.accent}; }
        .msg-urgent { color: ${colors.bg}; font-weight: 600; }
        .msg-urgent .msg-hint, .msg-urgent .msg-expired { color: rgba(23,22,28,0.7); }
        .msg-body { font-size: 14px; line-height: 1.4; }
        .msg-expired { font-size: 11px; color: ${colors.textMuted}; margin-top: 2px; }
        .msg-hint { font-size: 11px; color: ${colors.textMuted}; margin-top: 2px; font-style: italic; }
        .msg-voice-dot { margin-right: 4px; font-size: 10px; opacity: 0.85; }
        .msg-send-error {
          color: ${colors.danger};
          font-size: 12px;
          padding: 4px 8px;
          text-align: center;
        }
        .msg-composer {
          display: flex;
          align-items: flex-end;
          gap: 8px;
          padding: 8px 4px 0;
          border-top: 1px solid ${colors.divider};
          margin-top: 4px;
        }
        .msg-composer-input {
          flex: 1;
          background: ${colors.surface};
          color: ${colors.text};
          border: 1px solid ${colors.divider};
          border-radius: 18px;
          padding: 10px 14px;
          font-size: 15px;
          font-family: inherit;
          min-height: 40px;
          outline: none;
        }
        .msg-composer-input:focus { border-color: ${colors.accent}; }
        .msg-composer-input:disabled { opacity: 0.6; }
        .msg-send-btn {
          width: 40px; height: 40px;
          border-radius: 50%;
          border: none;
          background: ${colors.accent};
          color: ${colors.bg};
          display: flex; align-items: center; justify-content: center;
          cursor: pointer;
          transition: transform 80ms ease, opacity 120ms ease;
          flex-shrink: 0;
        }
        .msg-send-btn:active { transform: scale(0.92); }
        .msg-send-btn:disabled {
          background: ${colors.divider};
          color: ${colors.textMuted};
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
}

type RowProps = {
  urgent: boolean;
  direct: boolean;
  toSelf: boolean;
  fromSelf: boolean;
  canWhisperBack: boolean;
  hasAudio: boolean;
  bubbleBg: string;
  accentStripe: string;
  onReplay: () => void;
  onWhisperBack?: () => void;
  children: React.ReactNode;
};

function MessageRowButton({
  urgent,
  direct,
  toSelf,
  fromSelf,
  canWhisperBack,
  hasAudio,
  bubbleBg,
  accentStripe,
  onReplay,
  onWhisperBack,
  children,
}: RowProps) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const firedRef = useRef(false);

  const start = () => {
    if (!canWhisperBack) return;
    firedRef.current = false;
    timerRef.current = setTimeout(() => {
      firedRef.current = true;
      timerRef.current = null;
      try {
        navigator.vibrate?.(20);
      } catch {}
      onWhisperBack?.();
    }, LONG_PRESS_MS);
  };
  const cancel = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  return (
    <button
      className={`msg-row ${urgent ? 'msg-urgent' : ''} ${direct ? 'msg-direct' : ''} ${
        toSelf ? 'msg-to-self' : ''
      }`}
      style={{
        background: bubbleBg,
        boxShadow: [
          urgent ? null : `inset ${fromSelf ? '-3px' : '3px'} 0 0 ${accentStripe}`,
          direct && !toSelf && !urgent ? `inset 0 0 0 1px ${colors.danger}` : null,
          toSelf && !urgent ? `inset 0 0 0 1px ${colors.accent}` : null,
        ]
          .filter(Boolean)
          .join(', ') || undefined,
      }}
      onClick={() => {
        if (firedRef.current) {
          firedRef.current = false;
          return;
        }
        if (hasAudio) onReplay();
      }}
      onPointerDown={start}
      onPointerUp={cancel}
      onPointerLeave={cancel}
      onPointerCancel={cancel}
      onContextMenu={(e) => {
        if (!canWhisperBack) return;
        e.preventDefault();
        cancel();
        firedRef.current = true;
        onWhisperBack?.();
      }}
      disabled={!hasAudio && !canWhisperBack}
    >
      {children}
    </button>
  );
}
